import { useState, useMemo } from 'react'
import Navbar from '../features/Navbar'
import Footer from '../features/landing/Footer'
import PromoBanner from '../features/PromoBanner'
import PlanCarousel from '../features/pricing/PlanCarousel'
import { pricingPlans } from '../features/pricing/data/pricingPlans.js'
import PlanFeatures from '../features/pricing/PricingFeature.jsx'
import { useAuth } from '../features/auth/AuthContext'

const PRICING_FAQS = [
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. You can cancel your subscription at any time. Your plan remains active until the end of your current billing period.',
  },
  {
    q: 'Can I switch plans later?',
    a: 'Yes. You can upgrade or downgrade your plan at any time. Changes are applied automatically, with prorated billing where applicable.',
  },
  {
    q: 'When will I be billed?',
    a: 'Monthly plans are billed every month. Annual plans are billed upfront.',
  },
]

const CheckIcon = () => (
  <div className="w-[20px] h-[20px] rounded-full bg-brand-primary flex items-center justify-center shrink-0">
    <svg width="10" height="8" viewBox="0 0 14 10" fill="none">
      <path d="M1 5L5 9L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
)

const TrustBullets = () => (
  // Set #4, Desktop 2: 8px from "Make every deal..." at md+; mobile untouched, he only
  // specified this gap under Desktop.
  <div className="mx-auto w-fit flex flex-row flex-wrap items-center justify-center gap-x-[30px] gap-y-3 mt-6 md:mt-2">
    <div className="flex items-center gap-1">
      <CheckIcon />
      <span className="text-[12px] leading-[20px] text-brand-dark">7-day free trial</span>
    </div>
    {/* Микола: hide "Analyze up to 5 properties" on mobile */}
    <div className="hidden sm:flex items-center gap-1">
      <CheckIcon />
      <span className="text-[12px] leading-[20px] text-brand-dark">Analyze up to 5 properties</span>
    </div>
    <div className="flex items-center gap-1">
      <CheckIcon />
      <span className="text-[12px] leading-[20px] text-brand-dark">No credit card required</span>
    </div>
  </div>
)

const PricingToggle = ({ billing, setBilling }) => (
  <div className="inline-flex items-center p-[4px] rounded-full border border-black/10 bg-white">
    <button
      onClick={() => setBilling('monthly')}
      className={`h-[40px] px-5 rounded-full text-[14px] font-bold leading-[24px] transition-all duration-200 ${
        billing === 'monthly' ? 'bg-brand-primary text-white' : 'text-brand-dark/60 hover:text-brand-dark'
      }`}
    >
      Monthly
    </button>
    <button
      onClick={() => setBilling('annual')}
      className={`h-[40px] px-6 rounded-full text-[14px] font-bold leading-[24px] transition-all duration-200 ${
        billing === 'annual' ? 'bg-brand-primary text-white' : 'text-brand-dark/60 hover:text-brand-dark'
      }`}
    >
      Annual (Save Up To 50%)
    </button>
  </div>
)

const PricingFaq = () => {
  const [openIndex, setOpenIndex] = useState(0)
  const toggle = (i) => setOpenIndex((c) => (c === i ? -1 : i))

  return (
    // Set #4: 72px from the table's last row on mobile, 64px on desktop (already matched
    // before this pass — see regression-checklist.md).
    <section className="w-full mt-[72px] md:mt-[64px] flex flex-col md:flex-row gap-8 md:gap-12 text-left">
      {/* M2.7: "Common Questions," must not wrap. */}
      <h2 className="w-full md:w-[352px] text-[36px] leading-[48px] font-extrabold text-brand-dark md:shrink-0 whitespace-nowrap">
        Common Questions,<br />
        <span className="text-brand-primary">Clear Answers</span>
      </h2>

      <div className="w-full md:w-[732px] flex flex-col">
        {PRICING_FAQS.map(({ q, a }, i) => {
          // D1.1.1-new: the first question sat 24px lower than the heading beside it, because
            // every row carries the same vertical padding. Dropped on the first row from md
            // up, where the two columns are actually side by side.
            const isOpen = openIndex === i
          return (
            <div key={q} className="border-b border-brand-fieldBdr py-6 md:first:pt-0 flex flex-col gap-3">
              <button
                type="button"
                onClick={() => toggle(i)}
                aria-expanded={isOpen}
                className={`flex items-center justify-between text-left ${
                  isOpen ? 'text-brand-primary' : 'text-brand-dark'
                }`}
              >
                <span className="text-[20px] leading-[32px] font-bold">{q}</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M4 10h12" />
                  {!isOpen && <path d="M10 4v12" />}
                </svg>
              </button>
              {isOpen && <p className="text-[16px] leading-[28px] text-brand-dark">{a}</p>}
            </div>
          )
        })}
      </div>
    </section>
  )
}

const Pricing = () => {
  const [billing, setBilling] = useState('annual')
  // M2.2: the swiped-to card and the table's mobile column are now one piece of state,
  // so it lives here rather than inside PlanFeatures. Starts on the first card because
  // that is the one the carousel opens on — pre-scrolling to Pro would hide the fact
  // that there is a card to the left of it.
  const [selectedPlan, setSelectedPlan] = useState('starter')
  // D2.5.2: desktop only, null whenever the cursor is off the cards.
  const [hoveredPlan, setHoveredPlan] = useState(null)
  const plans = useMemo(() => pricingPlans[billing], [billing])
  const { user } = useAuth()
  const subStatus = user?.subscription?.status
  const isPaidSubscriber = subStatus === 'active' || subStatus === 'past_due'

  return (
    // D2.1: this page used to sit inside PageShell, whose max-w-canvas also capped the
    // navbar, banner and footer at 1200px — so on a wide screen they were the only place
    // in the app where the chrome stopped short of the edges. Home never used the shell;
    // Navbar and Footer already centre their own content, so the wrapper only did harm.
    <div className="w-full bg-brand-pageFill">
      <Navbar />
      <PromoBanner />

      {/* Set #4, Desktop 1 + 3 / Mobile 2: heading 48px from the banner at every width (was
          64px at md — the 48px mobile value was already right); FAQ-to-footer 64px desktop
          (was 120).
          14.08 followup, Mobile 1: FAQ-to-footer raised to 72px on mobile (was 48) — this
          section closes right after PricingFaq, so its own bottom padding is what governs
          that gap; Faq.jsx (Home) is untouched, this is Pricing's own component. */}
      <section className="bg-brand-pageFill pt-[48px] pb-[72px] md:pb-[64px]">
        <div className="max-w-canvas mx-auto px-6 md:px-[120px] flex flex-col items-center text-center">
          <h1 className="text-[36px] leading-[42px] md:text-[56px] md:leading-[56px] tracking-[-1px] md:tracking-[-2px] font-extrabold text-brand-dark">
            Our Pricing Plans
          </h1>

          <p className="mt-4 text-[20px] md:text-[24px] leading-[28px] md:leading-[36px] font-bold text-brand-dark">
            Make every deal a calculated, confident decision.
          </p>

          {/* D2.2: the checkmarks were hidden for anyone signed in, which is why Микола
              could not see them. They are back for visitors and trial users; someone who
              already pays is the one person "7-day free trial / no credit card required"
              would be wrong for, so they stay hidden there. */}
          {!isPaidSubscriber && <TrustBullets />}

          <div className="mt-8">
            <PricingToggle billing={billing} setBilling={setBilling} />
          </div>

          <PlanCarousel
            plans={plans}
            billing={billing}
            onSelect={setSelectedPlan}
            onHover={setHoveredPlan}
          />

          <PlanFeatures selectedPlan={selectedPlan} hoveredPlan={hoveredPlan} />

          <PricingFaq />
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default Pricing
